import { SOFT_OPT_OUT_DISTRIBUTION_MODELS } from '@/composables/payments/booksy-pay/consts'
import type { BooksyPayDistributionModel } from '@/composables/payments/booksy-pay/consts'

import {
  BOOKSY_PAY_ENTRY_TARGETS,
  type BooksyPayEntryTarget,
} from './consts'

export type { BooksyPayEntryTarget }
export { BOOKSY_PAY_ENTRY_TARGETS }

type BooksyPayStore = {
  state: {
    business: {
      booksyPaySettings: unknown
    }
  }
  getters: {
    'identity/isOwner': boolean
    'business/isBooksyPayEnabled': boolean
    'business/isBooksyPayAllowed': boolean
    'business/booksyPayDistributionModel': BooksyPayDistributionModel | null
  }
  dispatch: (action: string) => Promise<unknown>
}

type BooksyPayRoute = {
  params?: Record<string, unknown>
  query?: Record<string, unknown>
}

type EntryContext = {
  store: BooksyPayStore
  route: BooksyPayRoute
}

const ensureBooksyPaySettingsLoaded = async (store: BooksyPayStore): Promise<void> => {
  if (!store.state.business.booksyPaySettings) {
    await store.dispatch('business/loadBooksyPaySettings')
  }
}

export const getBooksyPayOnboardingRoute = ({ route }: EntryContext): Record<string, unknown> => ({
  name: 'settings.payments.booksy-pay.onboarding',
  params: {
    businessId: route?.params?.businessId,
  },
  query: route?.query,
})

const getOptOutLandingPageRoute = ({ route }: EntryContext): Record<string, unknown> => ({
  name: 'settings.payments.booksy-pay.opt-out-landing-page',
  params: {
    businessId: route?.params?.businessId,
  },
  query: route?.query,
})

const getEntryRedirectRoute = ({
  targetView,
  store,
  route,
}: EntryContext & { targetView: BooksyPayEntryTarget }): Record<string, unknown> | null => {
  const isOwner = store.getters['identity/isOwner']
  const isBooksyPayEnabled = store.getters['business/isBooksyPayEnabled']
  const distributionModel = store.getters['business/booksyPayDistributionModel']

  if (!isOwner) {
    return { name: 'calendar' }
  }

  if (targetView === BOOKSY_PAY_ENTRY_TARGETS.ACTIVATION) {
    if (SOFT_OPT_OUT_DISTRIBUTION_MODELS.some((model) => model === distributionModel) && !isBooksyPayEnabled) {
      return getOptOutLandingPageRoute({ store, route })
    }

    return getBooksyPayOnboardingRoute({ store, route })
  }

  if (
    targetView === BOOKSY_PAY_ENTRY_TARGETS.OPT_OUT_LANDING_PAGE
    && !isBooksyPayEnabled
    && !SOFT_OPT_OUT_DISTRIBUTION_MODELS.some((model) => model === distributionModel)
  ) {
    return getBooksyPayOnboardingRoute({ store, route })
  }

  return null
}

export const resolveBooksyPayEntryRedirect = async ({
  targetView,
  store,
  route,
}: EntryContext & { targetView: BooksyPayEntryTarget }): Promise<Record<string, unknown> | null> => {
  await ensureBooksyPaySettingsLoaded(store)
  return getEntryRedirectRoute({ targetView, store, route })
}
